import type { ChatUserModel } from 'types/chat-users/chat-user';
import type { ChatUserConfigType } from 'types/chat/props';
import type { HistoryMessage, MessageModel } from 'types/chat/messages/message';

/** ~chars per token for budgeting Hebrew + JSON (conservative). */
const CHARS_PER_TOKEN_EST = 3.5;
const MAX_CONTEXT_TOKENS = 2000;
const MAX_WHATSAPP_LINES = 15;

export type CurrentContextPayload = {
  contact: {
    name: string;
    phone: string;
    status: {
      conversationOpen: boolean | null;
      workflowStatusLabel: string | null;
      workflowStatusCode: string | null;
    };
    tags: string[];
    customFields: Record<string, string | null | undefined>;
  };
  whatsappHistory: Array<{ line: string }>;
  agent: {
    name: string;
    role: string | null;
  };
};

function roleLabel(type: string | null | undefined, locale: 'he' | 'en'): string {
  if (type === 'incoming') return locale === 'he' ? 'לקוח' : 'Customer';
  if (type === 'outcoming') return locale === 'he' ? 'נציג' : 'Agent';
  return locale === 'he' ? 'אחר' : 'Other';
}

function formatMessageTime(m: HistoryMessage | MessageModel): string {
  const raw = m.DateCreate ?? m.DateCreatedOriginal ?? m.DateCreateRaw ?? (m as MessageModel).createdAt;
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}

function messageBodyForContext(m: HistoryMessage | MessageModel): string {
  const raw = (m.Message ?? (m as MessageModel).text ?? '').replace(/\s+/g, ' ').trim();
  if (raw) return raw.length > 500 ? `${raw.slice(0, 500)}…` : raw;
  const ft = m.FileType ?? (m as MessageModel).FileType;
  if (ft) return `[מדיה: ${ft}]`;
  return '[ללא טקסט]';
}

export function buildWhatsAppHistoryLines(
  messages: Array<HistoryMessage | MessageModel>,
  locale: 'he' | 'en',
): string[] {
  const last = messages.slice(-MAX_WHATSAPP_LINES);
  return last.map((m) => {
    const t = formatMessageTime(m);
    const role = roleLabel(m.Type as string | null | undefined, locale);
    const body = messageBodyForContext(m);
    return `[${t}] ${role}: ${body}`;
  });
}

export function buildCurrentContextPayload(params: {
  chatUser: ChatUserModel;
  chatUserConfig: ChatUserConfigType;
  conversationMessages: Array<HistoryMessage | MessageModel>;
  agentDisplayName: string;
  agentRole: string | null;
  locale: 'he' | 'en';
}): CurrentContextPayload {
  const { chatUser, chatUserConfig, conversationMessages, agentDisplayName, agentRole, locale } = params;
  const tags: string[] = [];
  if (chatUser.LableName) tags.push(chatUser.LableName);

  const customFields: Record<string, string | null | undefined> = {
    userNote: chatUserConfig.userNote,
    email: chatUserConfig.email,
    address: chatUserConfig.address,
    city: chatUserConfig.city,
    birthDate: chatUserConfig.birthDate,
    role: chatUserConfig.role,
    image: chatUserConfig.image,
  };

  const lines = buildWhatsAppHistoryLines(conversationMessages, locale);

  return {
    contact: {
      name: chatUser.FullName || [chatUser.FirstName, chatUser.LastName].filter(Boolean).join(' ') || '—',
      phone: chatUser.UserDisplayPhoneNumber ?? '—',
      status: {
        conversationOpen: chatUser.IsOpen ?? null,
        workflowStatusLabel: chatUser.flowStatus ?? null,
        workflowStatusCode: chatUser.flowStatusCode != null ? String(chatUser.flowStatusCode) : null,
      },
      tags,
      customFields,
    },
    whatsappHistory: lines.map((line) => ({ line })),
    agent: {
      name: agentDisplayName,
      role: agentRole,
    },
  };
}

export function stringifyContextForPrompt(payload: CurrentContextPayload): string {
  return JSON.stringify(payload, null, 0);
}

/** Truncate whole context string to ~maxTokens (estimated). */
export function truncateContextStringApprox(contextJson: string, maxTokens: number = MAX_CONTEXT_TOKENS): string {
  const maxChars = Math.floor(maxTokens * CHARS_PER_TOKEN_EST);
  if (contextJson.length <= maxChars) return contextJson;
  return `${contextJson.slice(0, maxChars - 20)}…[truncated]`;
}

export function buildUserQueryPayload(params: {
  includeContext: boolean;
  contextJsonTruncated: string;
  userQuestion: string;
}): string {
  const { includeContext, contextJsonTruncated, userQuestion } = params;
  const q = userQuestion.trim();
  if (!includeContext) return q;
  return `CURRENT_CONTEXT:\n${contextJsonTruncated}\n\nQUERY:\n${q}`;
}

export function buildAgentSystemPrompt(params: {
  agentFirstName: string;
  directPasteMode: boolean;
}): string {
  const { agentFirstName, directPasteMode } = params;
  const direct = directPasteMode
    ? `\nמצב ניסוח ישיר: החזר אך ורק את טקסט התשובה המוצעת ללקוח, ללא הקדמות או הערות צד. נסח בגוף ראשון מטעם הנציג. אל תכלול הסברים מטא.`
    : `\nמצב יועץ: נתח את המצב והצע כיוונים קצרים לנציג — אל תכתוב טקסט מוכן לשליחה ללקוח אלא אם הנציג ביקש במפורש.`;

  return [
    'אתה היועץ של הנציג בפאנל צד — לא אתה מדבר עם הלקוח הקצה.',
    `פנה אל הנציג ישירות (למשל: "כדאי לך...", "שים לב ש..."). הנציג: ${agentFirstName}.`,
    'ענה תמיד בעברית בלבד, בשפה טבעית ומקצועית, בקצרה ופרקטית.',
    'אל תפנה ללקוח בגוף שני; אל תכתוב הודעות כאילו אתה השולח ללקוח.',
    direct,
  ].join('\n');
}

/**
 * System prompt for the global in-app technical support assistant (entire app — not chat reply drafting).
 */
export function buildAppTechnicalSupportSystemPrompt(params: {
  locale: 'he' | 'en';
  currentPath: string;
  agentDisplayName: string;
  agentRole: string | null;
}): string {
  const { locale, currentPath, agentDisplayName, agentRole } = params;
  const role = agentRole?.trim() || (locale === 'he' ? 'לא ידוע' : 'unknown');
  if (locale === 'he') {
    return [
      'אתה עוזר תמיכה טכנית ליישום FUNNER — מערכת לניהול שיחות, וואטסאפ, אנשי קשר, תבניות, אוטומציות ועוד.',
      'המשתמש הוא עובד בחברה (נציג/מנהל) — לא לקוח קצה. עזור בניווט, הגדרות, שגיאות נפוצות, ומיטב שימוש במערכת.',
      'ענה בקצרה, בצעדים ברורים. אם אינך בטוח, הצע לבדוק במרכז מידע, או לפנות לתמיכה — אל תמציא מידע פרטי על חשבונות או נתוני לקוחות.',
      `המיקום הנוכחי ביישום (path): ${currentPath}`,
      `נציג: ${agentDisplayName} · תפקיד: ${role}`,
      'התאם את שפת התשובה לשפת השאלה (עברית/אנגלית).',
    ].join('\n');
  }
  return [
    'You are a technical support assistant for the FUNNER application: business messaging, WhatsApp, contacts, templates, and automations.',
    'The user is a company employee (agent/admin), not an end customer. Help with navigation, settings, common errors, and how to use features.',
    'Be concise; use clear steps. If unsure, suggest the in-app help center or contacting support — do not invent private customer or billing details.',
    `Current app path: ${currentPath}`,
    `User: ${agentDisplayName} · Role: ${role}`,
    "Match the user's language (Hebrew or English).",
  ].join('\n');
}
